import React, { useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { AgentAvatarSize } from '../shared/AgentPresenceOutline.tsx';

const styles = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
	},
	blockRoot: { display: 'flex' },
	small: { width: '24px', height: '27px' },
	medium: { width: '32px', height: '35px' },
	overlapped: { marginInlineStart: token('space.negative.075') },
});

type AgentPresenceAvatarFrameProps = Pick<
	React.HTMLAttributes<HTMLDivElement>,
	'onLoadCapture' | 'onErrorCapture'
> & {
	children?: React.ReactNode;
	size?: AgentAvatarSize;
	isBlock?: boolean;
	isOverlapped?: boolean;
	stackIndex?: number;
	testId?: string;
};

export function AgentPresenceAvatarFrame({
	children,
	size,
	isBlock,
	isOverlapped,
	stackIndex,
	onLoadCapture,
	onErrorCapture,
	testId = 'agent-presence-avatar',
}: AgentPresenceAvatarFrameProps): React.JSX.Element {
	const style = useMemo(() => ({ zIndex: stackIndex }), [stackIndex]);
	return (
		<Box
			xcss={cx(
				size && styles.root,
				isBlock && styles.blockRoot,
				size && styles[size],
				isOverlapped && styles.overlapped,
			)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Preserves the runtime stacking order used by the facepile.
			style={style}
			onLoadCapture={onLoadCapture}
			onErrorCapture={onErrorCapture}
			testId={testId}
		>
			{children}
		</Box>
	);
}
